import { get, isEmpty, join, map, sumBy } from 'lodash'
import { Decimal } from 'decimal.js'

export const formatCartItemModifiers = (modifierItems, notes) => {
  const modifierStrings = map(modifierItems, (modifierItem) => {
    const name = get(modifierItem, 'name', '')
    const priceInCents = get(modifierItem, 'defaultPriceInCents', 0)

    if (modifierItem.refund) {
      return ''
    }

    if (priceInCents > 0) {
      return `${name} +$${(priceInCents / 100).toFixed(2)}`
    }

    return name
  })

  let result = join(
    modifierStrings.filter((str) => !!str),
    ', ',
  )

  if (!isEmpty(result) && !isEmpty(notes)) result += '. '
  if (!isEmpty(notes)) result += notes

  return result
}

export const centsToDollars = (cents) => {
  let result = `$${(Math.abs(cents) / 100).toFixed(2)}`
  if (cents < 0) result = `-${result}`

  return result
}

export const centsToFixedDollars = (cents) => {
  const dollars = Math.abs(cents) / 100
  const formatted = dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)

  if (cents < 0) return `-$${formatted}`

  return `$${formatted}`
}

/**
 * return string amount in cents without rounding down or up | This will return any negative values positive
 * @param {?String} stringAmount an amount $1.00 or 1.00
 * @returns {Number} amount in cents
 */
export const stringToCents = (val) => {
  if (val === undefined || val === null || val === '') {
    return 0
  }

  const stringAmount = typeof val === 'number' ? val.toString() : val
  const regex = /[^0-9.]/g // removes everything but period and number
  const stringToCents = new Decimal(stringAmount.replace(regex, '')).mul(100).toNumber()

  return parseInt(stringToCents)
}

export const toFixed = (number, digits) => {
  const regex = /^[0]{0,}\./
  let result = number
  if (parseInt(number) !== number) {
    result = parseFloat(number.toFixed(digits))
  }

  return result.toString().replace(regex, '.')
}

export const stringFloatToCents = (stringFloat) => {
  // The leading negative shouldn't be removed, as it is part of the value.
  const isNegative = stringFloat && stringFloat?.length !== 0 ? stringFloat.trim().charAt(0) === '-' : false
  const multiplier = isNegative ? -1 : 1

  return multiplier * parseInt(stringFloat?.replace?.(/\D/g, '') ?? 0)
}

export const centsToFloat = (cents) => (cents / 100).toFixed(2)

export const getTicketIdFromResult = (barCodeData) => {
  const formattedData =
    barCodeData?.split('|')?.reduce((dataObj, keyValue) => {
      const [key, value] = keyValue?.split('=') ?? []
      return {
        ...dataObj,
        [key]: value,
      }
    }, {}) ?? {}
  return formattedData['TBN']
}

export const displayPreOrdersItemPrice = (cartItem, modifiers) => {
  const basePrice = get(cartItem, 'price', '0.00')
  const priceInCents = stringFloatToCents(basePrice)

  let totalModPrice = 0
  modifiers.forEach((mod) => {
    //if we're calculating mod prices for a refunded item
    if (!cartItem.removedAt) {
      //we only want to add the price of mods that are refunded
      if (!mod.removedAt) {
        totalModPrice += stringFloatToCents(mod.price || '0.00')
      }
    } else {
      //We're calculating price of a non refunded item we need to accumulate only the price of non refunded mods
      if (!!mod.removedAt) {
        totalModPrice += stringFloatToCents(mod.price || '0.00')
      }
    }
  })

  const price = priceInCents + totalModPrice

  return `$${(price / 100).toFixed(2)}`
}

export const displayPrice = (cartItem, modifiers) => {
  //TODO: T40311 check priceInCents is not in the cartItems schema it's mapped after order details fetch
  const basePrice = get(cartItem, 'priceInCents', 0)
  const modifierPrice = sumBy(modifiers, (mod) => get(mod, 'defaultPriceInCents', 0))
  const price = basePrice + modifierPrice

  return `$${(price / 100).toFixed(2)}`
}

// Handles response messages that are returned as Ruby objects
export const parseRubyHash = (str) => {
  const sanitized = str
    .replace(/=>/g, ':')
    .replace(/:([a-zA-Z0-9_]+)/g, '"$1"')
    .replace(/"([a-zA-Z0-9_]+)":/g, '"$1":')

  try {
    return JSON.parse(sanitized)
  } catch (e) {
    return str
  }
}
